import axios, { AxiosError } from "axios";
import i18n from "../../i18n";
import router from "../router";
import { useSessionConflictStore } from "../store/useSessionConflict";
import { useNotifications } from "../store/useNotifications";
import { createInflightDedup } from "../common/helpers/inflightDedup";
import { extractReadableError } from "../common/helpers/extractReadableError";
import { isExpiredSearchResultsEnvelope } from "../common/helpers/eplex/searchResultsExpired";
import { teardownSignalWidget } from "../common/helpers/signalWidget";
import { csrfHeader } from "../common/helpers/auth/csrf";
import { isSessionRejected, markSessionRejected } from "../common/helpers/auth/authBrowserState";
import type { RetryAxiosRequestConfig } from "../models/api/RetryAxiosRequestConfig";
import { installMetrikaGoalsInterceptor } from "./metrikaGoalsInterceptor";

export { extractReadableError };

export interface EplexErrorResponse {
  status: boolean;
  error?: {
    code: number;
    message: string;
  };
  signature?: string;
  /**
   * Адрес страницы входа, собранный сервером (Ф1). Приходит с 401.
   *
   * `null` означает «уводить некуда»: либо контур не настроен, либо сработала
   * серверная анти-петля и это уже второй отказ подряд.
   */
  login_url?: string | null;
}

function redirectToLogin() {
  const route = router.currentRoute.value;
  if (route.path === "/login") return;

  // Сессия истекла — гасим внешний чат-виджет «Сигнал» сразу, не дожидаясь
  // перезагрузки: иначе им можно пользоваться на странице логина без входа.
  teardownSignalWidget();
  router.replace({ path: "/login", query: { redirect: route.fullPath } });
}

/**
 * Сообщение для уведомления при 401. Если бэк прислал понятный текст
 * (например, "Пользователь заблокирован") — показываем его, иначе общий fallback
 * про истекшую сессию.
 */
export function pickUnauthorizedMessage(data: EplexErrorResponse | undefined, fallback: string): string {
  return extractReadableError(data?.error?.message) ?? fallback;
}

const instanceJWT = axios.create({
  baseURL: "/api/",
  withCredentials: true,
  // 30s — защита от «зависших» запросов. Если бэк не успеет ответить, axios
  // бросит ECONNABORTED; глобальный interceptor возьмёт его в обработку,
  // а UI с активным «Копируем…» / другими loader'ами вернётся в нормальное
  // состояние (вместо вечного спиннера).
  timeout: 30_000,
});

delete instanceJWT.defaults.headers.common["XMLHttpRequest"];

instanceJWT.interceptors.request.use((config) => {
  // Заголовка Authorization здесь больше нет вовсе: сессия — это кука, и она уходит
  // сама. Токен из localStorage был мусором от прежнего входа, и отправка его
  // авторизовывала человека ПРЕДЫДУЩИМ пользователем (CAB-641).
  //
  // CSRF при токене сессии в куке (CAB-642). Заголовок уходит только на бэкенд ЛК —
  // на api.eplex.ru токен принимается заголовком, куки там игнорируются.
  Object.assign(config.headers, csrfHeader(config.method));

  return config;
});

/**
 * При единой авторизации (CAB-640) реакция на 401 ровно одна — увести на страницу
 * входа. Ни продлить, ни повторить, ни согласовать между вкладками: продлением
 * занимается БЭКЕНД, он смотрит остаток жизни токена на каждом запросе и сам
 * отдаёт новую пару в Set-Cookie. Любая попытка фронта помочь здесь либо
 * дублирует его работу, либо гонкой двух вкладок разлогинивает человека.
 */
function handleUnauthorizedError(data: EplexErrorResponse, error: AxiosError) {
  handleRejectedSession(data);

  return Promise.reject(error);
}

/**
 * Сессию, отвергнутую бэкендом, гасим ровно один раз на токен (CAB-680).
 *
 * Признаком «уже отреагировали» служит метка отклонённой сессии, а не флаг в памяти
 * модуля. Флаг здесь не работал дважды: при единой авторизации его снимала только
 * ветка с токеном из localStorage, которой в этой схеме нет вовсе, — поэтому первый
 * же 401 взводил его навсегда, и все последующие отказы кабинет глотал молча.
 * Метка же привязана к самому токену и гаснет сама при входе заново.
 *
 * Страница открытого кабинета шлёт запросы пачкой и по таймеру, так что без этого
 * человек получал либо десяток одинаковых уведомлений, либо — после залипшего
 * флага — ни одного, продолжая сидеть в кабинете, где не работает ничего.
 */
function handleRejectedSession(data: EplexErrorResponse): void {
  if (isSessionRejected()) return;

  markSessionRejected();
  // Одноразовая уборка следов прежней схемы: у людей эти ключи остались от старого
  // входа. Через релиз её можно снять — сессию они больше ни на что не влияют.
  localStorage.removeItem("access_token");
  localStorage.removeItem("_user");

  const action = resolveUnauthorizedAction(data);

  // Уходим сразу и без уведомления: человек и так увидит форму входа, а тост всё
  // равно не переживёт смену адреса
  if (action.kind === "redirect") {
    teardownSignalWidget();
    window.location.assign(action.url);

    return;
  }

  const { notify } = useNotifications();
  notify(pickUnauthorizedMessage(data, i18n.global.t("errors.sessionHasExpiredOrInvalid")), "error");
  redirectToLogin();
}

/**
 * Запросы, для которых 401 — не повод объявлять сессию мёртвой и уводить на вход.
 *
 * Выход из ЛК здесь главный (CAB-680). Его зовёт как раз тот человек, у которого
 * сессия УЖЕ недействительна, и перехват его 401 превращал единственное лечащее
 * действие в ещё одно уведомление «сессия истекла или недействительна» — ровно то,
 * что видно на скриншоте в задаче при нажатии кнопки «Выйти».
 *
 * «Выйти на всех устройствах» сюда НЕ входит намеренно: эта операция требует живой
 * сессии, и честный ответ на её 401 — увести человека на страницу входа.
 */
export function isSessionErrorExempt(url: string): boolean {
  return /(^|\/)auth\/(unified\/)?logout$/.test(url);
}

/**
 * Что делать с отвергнутой сессией. Решение вынесено отдельно от исполнения, чтобы
 * его можно было проверить тестом: цена ошибки здесь — либо человек мигает
 * редиректами, либо остаётся в кабинете, где не работает ничего.
 */
export type UnauthorizedAction = { kind: "redirect"; url: string } | { kind: "login-screen" };

export function resolveUnauthorizedAction(data: EplexErrorResponse | undefined): UnauthorizedAction {
  const loginUrl = data?.login_url;

  // Адрес входа собирает сервер (Ф1) — фронт не знает его вовсе. Пришёл — уходим.
  if (typeof loginUrl === "string" && loginUrl.length > 0) {
    return { kind: "redirect", url: loginUrl };
  }

  // Уводить некуда: контур не настроен либо сервер погасил вторую попытку подряд
  // (Ф2). Показываем экран входа, а не мигаем редиректом.
  return { kind: "login-screen" };
}

/**
 * Эндпоинты, где лимит частоты — рабочая ситуация, а не ошибка пользователя:
 * запрос повторяем тихо, без модалки «слишком частые запросы».
 *
 * `group_search` попал сюда из-за выгрузки проценки: цены бэк отдаёт только
 * построчно, поэтому один экспорт — это пачка запросов подряд, и лимит на ней
 * срабатывает штатно (CAB-727).
 */
const RATE_LIMIT_RETRY_ENDPOINTS = [
  "eplex/search_live",
  "eplex/orders/journal",
  "eplex/billing",
  "eplex/group_search",
  // Настройки пользователя (наценки, CAB-778): апстрим режет ~3 запроса в секунду,
  // а сохранение фоновое — модалка «слишком много запросов» тут только пугает
  "eplex/settings/common",
];
const RATE_LIMIT_MAX_RETRIES = 5;
const RATE_LIMIT_RETRY_DELAY_MS = 1200;

/** Повторяем ли 429 по этому адресу тихо, без модалки. */
export function shouldAutoRetryRateLimit(url: string): boolean {
  return RATE_LIMIT_RETRY_ENDPOINTS.some((ep) => url.includes(ep));
}

const TOKEN_EXPIRED_MAX_RETRIES = 3;
const TOKEN_EXPIRED_RETRY_DELAY_MS = 500;

/**
 * Иногда proxy lk_eplex_api оборачивает истекший токен в 500-ответ:
 *   { code: 500, message: 'Неопознанная ошибка Data: {"error_code":419,"err":"Token expired"}' }
 * Это поведение нестабильное (видим только в проде) — поэтому пробуем тихо повторить
 * запрос, прежде чем разлогинить пользователя.
 */
export function isTokenExpiredEnvelope(data: EplexErrorResponse | undefined): boolean {
  const message = data?.error?.message;
  if (typeof message !== "string" || message.length === 0) return false;
  if (message.includes("Token expired")) return true;
  if (/"error_code"\s*:\s*419\b/.test(message)) return true;
  return false;
}

/**
 * Eplex API иногда отвергает пересланный токен раньше, чем истекает его срок:
 * замерено на проде 28.08.2026 — апстрим отвечает отказом примерно за 140 секунд до
 * `exp`. С работающим продлением (порог 5 минут) это не должно случаться вовсе, но
 * если случилось — повторить стоит: ЭТОТ ЖЕ запрос на своём ответе разбудил продление
 * пары на бэкенде, и повтор уйдёт уже с новой кукой.
 *
 * Повтор ровно один. Дальше это настоящий отказ, и маскировать его нельзя.
 */
async function handleTokenExpired(
  data: EplexErrorResponse,
  originalRequest: RetryAxiosRequestConfig,
  error: AxiosError,
): Promise<unknown> {
  const retryCount = originalRequest._tokenExpiredRetryCount ?? 0;
  if (retryCount < TOKEN_EXPIRED_MAX_RETRIES) {
    originalRequest._tokenExpiredRetryCount = retryCount + 1;
    await new Promise<void>((r) => setTimeout(r, TOKEN_EXPIRED_RETRY_DELAY_MS));
    return instanceJWT(originalRequest);
  }
  return handleUnauthorizedError(data, error);
}

async function handleRateLimit(
  message: string,
  originalRequest: RetryAxiosRequestConfig,
  error: AxiosError,
): Promise<unknown> {
  const url = originalRequest.url ?? "";
  const shouldAutoRetry = shouldAutoRetryRateLimit(url);

  if (shouldAutoRetry) {
    const retryCount = originalRequest._rateLimitRetryCount ?? 0;
    if (retryCount < RATE_LIMIT_MAX_RETRIES) {
      originalRequest._rateLimitRetryCount = retryCount + 1;
      await new Promise<void>((r) => setTimeout(r, RATE_LIMIT_RETRY_DELAY_MS));
      return instanceJWT(originalRequest);
    }
  }

  const sessionStore = useSessionConflictStore();
  const action = await sessionStore.showRateLimit(message);

  if (action === "retry") {
    originalRequest._rateLimitRetryCount = 0;
    return instanceJWT(originalRequest);
  }

  return Promise.reject(error);
}

async function handleSessionConflict(
  message: string,
  originalRequest: RetryAxiosRequestConfig,
  error: AxiosError,
): Promise<unknown> {
  const sessionStore = useSessionConflictStore();
  const action = await sessionStore.showConflict(message);

  if (action === "retry") {
    return instanceJWT(originalRequest);
  }

  // Та же одноразовая уборка следов прежней схемы, что и в handleRejectedSession
  localStorage.removeItem("access_token");
  localStorage.removeItem("_user");
  redirectToLogin();
  return Promise.reject(error);
}

/**
 * 403 при единой авторизации означает «нет прав» либо расхождение маркера защиты.
 * Повторять запрос с тем же состоянием бессмысленно, а прежний путь — продлить сессию
 * своими руками через keepalive — исчез вместе со старой схемой (CAB-640, CAB-642).
 */
function handleForbiddenError(error: AxiosError) {
  return Promise.reject(error);
}

instanceJWT.interceptors.response.use(
  async (response) => {
    const data = response.data as EplexErrorResponse;

    if (data?.signature === "lk_eplex_api" && data?.error) {
      const originalRequest = response.config as RetryAxiosRequestConfig;
      const url = originalRequest.url ?? "";

      if (url.includes("eplex/session/reset")) {
        return response;
      }

      if (data.error.code === 403) {
        return handleSessionConflict(
          data.error.message,
          originalRequest,
          new AxiosError("Session conflict"),
        ) as Promise<never>;
      }

      if (data.error.code === 429) {
        return handleRateLimit(data.error.message, originalRequest, new AxiosError("Rate limit")) as Promise<never>;
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    if (!error.response || !error.config) return Promise.reject(error);

    const originalRequest = error.config as RetryAxiosRequestConfig;
    const url = originalRequest.url ?? "";

    if (isSessionErrorExempt(url)) {
      return Promise.reject(error);
    }

    const status = error.response.status;
    const data = error.response.data as EplexErrorResponse;

    if (status === 401) {
      return handleUnauthorizedError(data, error);
    }

    if (status === 500 && data?.signature === "lk_eplex_api" && isTokenExpiredEnvelope(data)) {
      return handleTokenExpired(data, originalRequest, error);
    }

    // Конфликт сессий приезжает только как настоящий HTTP 403 (RedirectController отдаёт его
    // через ApiResponse::createResponse() без переопределения статуса). Прикладные 4xx от сервисов
    // за шлюзом приходят как HTTP 400, сохраняя error.code = 403 от апстрима — их нельзя принимать
    // за конфликт сессий, иначе на «нет прав» показывается модалка «выйти на всех устройствах».
    if (status === 403 && data.signature === "lk_eplex_api" && data.error?.code === 403) {
      return handleSessionConflict(data.error.message, originalRequest, error);
    }

    if (data.signature === "lk_eplex_api" && data.error?.code === 429) {
      return handleRateLimit(data.error.message, originalRequest, error);
    }

    if (status === 403) {
      return handleForbiddenError(error);
    }

    return Promise.reject(error);
  },
);

instanceJWT.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!error.response) return Promise.reject(error);

    const data = error.response.data as EplexErrorResponse;
    // не показываем англоязычное «Unknown error» — при отсутствии текста от бэка
    // подставляем локализованный fallback
    const errorMessage = extractReadableError(data.error?.message) ?? i18n.global.t("common.unexpectedError");

    if (data.signature === "lk_eplex_api" && (data.error?.code === 403 || data.error?.code === 429)) {
      return Promise.reject(error);
    }

    // Пока идут тихие ретраи на завернутый в 500 expired-token — не шумим тостом.
    // Если ретраи исчерпаются, handleUnauthorizedError сам покажет уведомление.
    const originalRequest = error.config as RetryAxiosRequestConfig | undefined;
    if (
      error.response.status === 500 &&
      data?.signature === "lk_eplex_api" &&
      isTokenExpiredEnvelope(data) &&
      (originalRequest?._tokenExpiredRetryCount ?? 0) <= TOKEN_EXPIRED_MAX_RETRIES
    ) {
      return Promise.reject(error);
    }

    // Фоновые polling-запросы помечают _silent: true — не пугаем пользователя
    // временными network-ошибками upstream (например "Could not resolve host").
    if (originalRequest?._silent) {
      return Promise.reject(error);
    }

    // «Результаты проценки больше не доступны» (истёк срок хранения на бэке) —
    // это не сбой, а устаревшие данные. Тост тут бесполезен и пугает (а при
    // параллельных запросах приезжает пачкой); объясняет ситуацию сам экран.
    if (isExpiredSearchResultsEnvelope(data)) {
      return Promise.reject(error);
    }

    if (error.response.status === 500 || error.response.status === 403 || error.response.status === 429) {
      const { notify } = useNotifications();
      notify(errorMessage, "error");
    }

    return Promise.reject(error);
  },
);

// JS-цели Метрики по успешным ответам — последним, после обработчиков ошибок сессии
installMetrikaGoalsInterceptor(instanceJWT);

/**
 * In-flight дедуп одинаковых GET-запросов. На загрузке страницы разные точки кода
 * (например страница поставщиков и виджет процессов в сайдбаре) параллельно дёргают
 * один и тот же эндпоинт (installed?loaded=1) — вместо двух сетевых запросов уходит
 * один общий. Запись живёт только на время запроса, поэтому это не кэш (без staleness).
 */
export const dedupedGet = createInflightDedup((url: string) => instanceJWT.get(url));

export default instanceJWT;
